import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import type { GroupChannel } from '@sendbird/chat/groupChannel';

import { chatParticipantsSelector } from '@/domain/chat';
import { userProfileSelector } from '@/domain/env';
import { ChatUser } from '@/components/mui/Layouts/components/Chat/types';
import UserSelectModal from '@/components/mui/Layouts/components/Chat/components/UserSelectModal';

interface Props {
  channel: GroupChannel;
  onCancel: () => void;
  onInvited?: () => void;
}

const InviteChannelModal: React.FC<Props> = ({ channel, onCancel, onInvited }) => {
  const { formatMessage } = useIntl();
  const chatUsers = useSelector(chatParticipantsSelector);
  const userProfile = useSelector(userProfileSelector);

  const existingMemberIds = useMemo(
    () => new Set(channel.members.map((m) => m.userId)),
    [channel.members],
  );

  const users: ChatUser[] = useMemo(
    () =>
      chatUsers
        .filter(
          (u: ChatUser) =>
            Boolean(u.userGUID) && u.userGUID !== userProfile.id && !existingMemberIds.has(u.userGUID),
        )
        .toArray(),
    [chatUsers, userProfile.id, existingMemberIds],
  );

  const handleSubmit = useCallback(
    async (selectedIds: string[]) => {
      await channel.inviteWithUserIds(selectedIds);
      // Refresh so the header/channel-list titles re-derive with the new member (DA-19568).
      // Fire-and-forget best-effort: the invite already succeeded server-side, so don't block the
      // modal on it — close immediately and let titles update when the refresh lands (header/list
      // also recover via Sendbird events; onInvited refetches the settings panel).
      void channel.refresh().catch((e) => {
        console.error('[InviteChannelModal] channel.refresh() after invite failed:', e);
      });
      // Close before onInvited: onInvited remounts the settings subtree (unmounting the
      // ModerationPanel that owns onCancel's state), so onCancel must run while it's still mounted.
      onCancel();
      onInvited?.();
    },
    [channel, onCancel, onInvited],
  );

  return (
    <UserSelectModal
      title={formatMessage({ id: 'chat.inviteChannel.title', defaultMessage: 'Select members' })}
      submitLabel={formatMessage({ id: 'chat.inviteChannel.invite', defaultMessage: 'Invite' })}
      users={users}
      maxSelectable={Math.max(0, 12 - channel.members.length)}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    />
  );
};

export default InviteChannelModal;
